import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

const MAX_FILE_BYTES = 10 * 1024 * 1024
const FILE_ACCEPT = '.pdf,.step,.stp,.dwg,.dxf,.igs,.iges'

function formatFileNames(files) {
  return Array.from(files, (file) => file.name).join(', ')
}

export default function Contact({ quotePortalUrl = import.meta.env.VITE_QUOTE_PORTAL_URL || 'YOUR_QUOTE_PORTAL_URL' } = {}) {
  const [sent, setSent] = useState(false)
  const [files, setFiles] = useState('')
  const [fileError, setFileError] = useState('')
  const location = useLocation()
  const instantPortal = /^https:\/\//.test(quotePortalUrl)

  useEffect(() => {
    setSent(new URLSearchParams(location.search).get('sent') === '1')
  }, [location.search])

  function handleFilesChange(event) {
    const selected = event.currentTarget.files
    const totalBytes = Array.from(selected).reduce((total, file) => total + file.size, 0)

    if (totalBytes > MAX_FILE_BYTES) {
      event.currentTarget.value = ''
      setFiles('')
      setFileError('Files are over 10 MB total. Please send fewer or smaller files, or email them to k-saai@machinexamerica.com.')
      return
    }

    setFileError('')
    setFiles(formatFileNames(selected))
  }

  return (
    <section className="grid grid-cols-[1.4fr_1fr] items-start gap-[72px] px-[120px] pt-[220px] tablet:grid-cols-1 tablet:px-6 tablet:pt-[116px]">
      <div className="flex min-w-0 flex-col gap-9">
        <div className="flex flex-col gap-5">
          <h1 className="m-0 font-display text-[64px] font-medium leading-[1.05] tracking-[-0.02em] tablet:text-[44px] tablet:leading-[1.08]">
            Request a quote
          </h1>
          <p id="sent-msg" role="status" className={`m-0 rounded-[14px] border border-mx-accent bg-[rgba(232,119,58,0.15)] px-5 py-4 text-[17px] text-mx-white ${sent ? '' : 'hidden'}`}>
            Thanks, your request is in. An engineer will reply within 24 hours.
          </p>
          <p className="m-0 max-w-[620px] text-[19px] leading-[1.6] text-[#C9CAE0]">
            Attach your drawing and tell us the quantities. An engineer reviews every request and replies within 24 hours.
          </p>
        </div>

        <div id="instant-quote" data-portal={quotePortalUrl} className="flex items-center justify-between gap-8 rounded-[24px] border border-mx-accent bg-[linear-gradient(135deg,rgba(232,119,58,0.18),rgba(16,19,74,0.9))] p-8 px-10 tablet:flex-wrap phone:p-6">
          <div className="flex flex-col gap-2">
            <span id="iq-label" className="font-display text-[13px] uppercase tracking-[0.14em] text-mx-accent-light">
              {instantPortal ? 'Instant quote' : 'Fast quote'}
            </span>
            <h2 id="iq-title" className="m-0 font-display text-[26px] font-semibold leading-[1.2] tablet:text-[32px]">
              {instantPortal ? 'Upload a CAD file, see your price in minutes' : 'Upload your drawing, get a quote within 24 hours'}
            </h2>
            <p id="iq-text" className="m-0 text-[15px] leading-[1.6] text-mx-copy">
              {instantPortal
                ? 'For machined parts: upload a STEP file, choose material, finish and quantity, and get pricing and lead time right away.'
                : 'Send a PDF, STEP or DWG file with your quantities. An engineer reviews it and sends a duty-paid price with lead time.'}
            </p>
          </div>
          <a
            id="instant-quote-btn"
            href={instantPortal ? quotePortalUrl : '#quote-form'}
            target={instantPortal ? '_blank' : undefined}
            rel={instantPortal ? 'noopener' : undefined}
            className="inline-flex min-h-[52px] shrink-0 items-center gap-[10px] rounded-[999px] bg-mx-accent px-7 font-display text-[15px] font-semibold tracking-[0.06em] text-mx-ink hover:bg-mx-accent-light hover:text-mx-ink phone:px-5"
          >
            {instantPortal ? 'Get instant price' : 'Upload your drawing'}
          </a>
        </div>

        <p id="iq-note" className={`${instantPortal ? '' : 'hidden'} -mt-3 mb-0 text-[15px] text-[#9A9CC4]`}>
          Prefer an engineer to review it first? Use the form below.
        </p>

        <form
          id="quote-form"
          action="https://formsubmit.co/k-saai@machinexamerica.com"
          method="POST"
          encType="multipart/form-data"
          className="grid grid-cols-2 gap-5 rounded-3xl border border-mx-border bg-mx-card p-10 tablet:grid-cols-1 phone:p-6"
        >
          <input type="hidden" name="_subject" value="MachineX America — New quote request" />
          <input type="hidden" name="_template" value="box" />
          <input type="hidden" name="_next" value="https://machinexamerica.com/contact.html?sent=1" />
          <input type="text" name="_honey" tabIndex="-1" autoComplete="off" aria-hidden="true" className="hidden" />

          <label className="flex flex-col gap-2 text-[14px] text-[#D9D8E8]">
            Full name
            <input name="name" required type="text" className="h-12 rounded-[10px] border border-mx-border-light bg-mx-ink px-[14px] font-sans text-base leading-[normal] text-mx-white" />
          </label>
          <label className="flex flex-col gap-2 text-[14px] text-[#D9D8E8]">
            Company
            <input name="company" type="text" className="h-12 rounded-[10px] border border-mx-border-light bg-mx-ink px-[14px] font-sans text-base leading-[normal] text-mx-white" />
          </label>
          <label className="flex flex-col gap-2 text-[14px] text-[#D9D8E8]">
            Work email
            <input name="email" required type="email" className="h-12 rounded-[10px] border border-mx-border-light bg-mx-ink px-[14px] font-sans text-base leading-[normal] text-mx-white" />
          </label>
          <label className="flex flex-col gap-2 text-[14px] text-[#D9D8E8]">
            Phone (optional)
            <input name="phone" type="tel" className="h-12 rounded-[10px] border border-mx-border-light bg-mx-ink px-[14px] font-sans text-base leading-[normal] text-mx-white" />
          </label>
          <label className="flex flex-col gap-2 text-[14px] text-[#D9D8E8]">
            Industry
              <select name="industry" className="box-border h-12 rounded-[10px] border border-mx-border-light bg-mx-ink px-[14px] font-sans text-base leading-[normal] text-mx-white">
              <option>Oil &amp; Gas</option>
              <option>Data Centers</option>
              <option>Industrial &amp; Process Equipment</option>
              <option>Other</option>
            </select>
          </label>
          <label className="flex flex-col gap-2 text-[14px] text-[#D9D8E8]">
            Annual quantity
            <input name="annual_quantity" type="text" placeholder="e.g. 2,000 pcs/year" className="h-12 rounded-[10px] border border-mx-border-light bg-mx-ink px-[14px] font-sans text-base leading-[normal] text-mx-white" />
          </label>
          <label className="col-span-full flex flex-col gap-2 text-[14px] text-[#D9D8E8] tablet:col-span-1">
            What do you need made?
            <textarea name="details" rows="4" placeholder="Part name, material, finish, target delivery date" className="resize-y rounded-[10px] border border-mx-border-light bg-mx-ink px-[14px] py-3 font-sans text-base leading-[normal] text-mx-white" />
          </label>

          <label className="relative col-span-full flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[14px] border border-dashed border-[#4A4F92] p-8 text-center text-[15px] text-[#D9D8E8] tablet:col-span-1">
            <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#F29A63" strokeWidth="1.8">
              <path d="M12 16V4" />
              <path d="M7 9l5-5 5 5" />
              <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
            </svg>
            <span><strong className="text-mx-accent-light">Upload drawings</strong> · PDF, STEP, DWG up to 10 MB total</span>
            <span id="file-list" aria-live="polite" className={`text-[14px] ${fileError ? 'text-mx-accent-light' : 'text-mx-copy'}`}>
              {fileError || files}
            </span>
            <span className="sr-only">Accepted file types: PDF, STEP, STP, DWG, DXF, IGS, and IGES.</span>
            <input
              type="file"
              name="drawings"
              multiple
              accept={FILE_ACCEPT}
              onChange={handleFilesChange}
              aria-label="Upload drawings"
              className="absolute h-px w-px opacity-0"
            />
          </label>

          <label className="col-span-full flex items-center gap-3 text-[15px] text-[#D9D8E8] tablet:col-span-1">
            <input type="checkbox" name="nda_requested" value="yes" className="box-border m-[3px_3px_3px_4px] h-5 w-5 accent-mx-accent" />
            Send us an NDA before we share files
          </label>
          <div className="col-span-full mt-1 flex justify-end tablet:col-span-1">
            <button type="submit" className="min-h-[52px] cursor-pointer rounded-[999px] border-0 bg-mx-accent px-8 font-display text-base font-semibold leading-[normal] tracking-[0.04em] text-mx-ink">
              Send request
            </button>
          </div>
        </form>
      </div>

      <aside className="mt-[184px] flex flex-col gap-10">
        <img
          src="/media/images/contact-drawing-review.jpg"
          alt="Engineer measuring a machined flange against its drawing"
          loading="lazy"
          className="block h-[280px] w-full rounded-[20px] border border-[#2A2E6E] object-cover"
        />
        <div className="flex flex-col gap-5">
          <h2 className="m-0 font-display text-2xl font-semibold leading-[normal] tablet:text-[32px]">What happens next</h2>
          <ol className="m-0 flex list-none flex-col gap-[18px] p-0">
            <li className="flex gap-4"><span className="w-5 font-display font-semibold text-mx-accent-light">1</span><span className="text-base leading-[1.6] text-mx-copy">We confirm receipt and sign an NDA if you ask for one.</span></li>
            <li className="flex gap-4"><span className="w-5 font-display font-semibold text-mx-accent-light">2</span><span className="text-base leading-[1.6] text-mx-copy">An engineer reviews the drawing and sends any questions.</span></li>
            <li className="flex gap-4"><span className="w-5 font-display font-semibold text-mx-accent-light">3</span><span className="text-base leading-[1.6] text-mx-copy">You receive a duty-paid quote with lead time.</span></li>
          </ol>
        </div>
        <div className="flex flex-col gap-[14px] border-t border-[#2A2E6E] pt-8 text-base leading-[1.6]">
          <a href="mailto:info@machinexamerica.com" className="font-medium text-[#F29A63]">info@machinexamerica.com</a>
          <span className="text-[#C9CAE0]">MachineX America LLC<br />5900 Balcones Drive, Suite 100<br />Austin, TX 78731</span>
        </div>
      </aside>
    </section>
  )
}
